import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { router } from './routes'
import { ApiError } from './api/errors'
import { ToastProvider } from './components/ui/Toast'
import { SessaoProvider } from './lib/SessaoProvider'
import { SESSAO_PREVIEW, type Sessao } from './lib/sessao'
import { request, getToken, clearSession, SESSAO_EXPIRADA } from './api/client'
import { AvisosPwa } from './components/AvisosPwa'
import { registrarServiceWorker } from './lib/pwa'
import './index.css'

/**
 * Política de retry: erro de rede/servidor tenta de novo; erro de negócio,
 * permissão ou validação não — insistir só irrita o usuário.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (tentativas, erro) => {
        if (erro instanceof ApiError) {
          if (!erro.recuperavel) return false
          if (erro.kind === 'siscomex_bloqueado') return false // respeita os ~60 s
        }
        return tentativas < 2
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

const EM_PREVIEW = import.meta.env.VITE_PREVIEW === '1'

/**
 * Carrega a sessão real de `GET /api/auth/me` antes de montar o roteador.
 * Sem token, ou com token inválido, cai em `/login`. No preview a sessão é
 * fixa (admin) para todas as telas ficarem visíveis.
 */
function Bootstrap() {
  const [sessao, setSessao] = useState<Sessao | null | undefined>(
    EM_PREVIEW ? SESSAO_PREVIEW : undefined,
  )

  useEffect(() => {
    if (EM_PREVIEW) return
    const sair = () => {
      clearSession()
      setSessao(null)
      if (window.location.pathname !== '/login') window.location.href = '/login'
    }
    window.addEventListener(SESSAO_EXPIRADA, sair)
    if (!getToken()) {
      setSessao(null)
      if (window.location.pathname !== '/login') window.location.replace('/login')
    } else {
      request<Sessao>('/api/auth/me')
        .then(setSessao)
        .catch((e) => {
          if (e instanceof ApiError && e.status === 401) sair()
          // Falha de rede: mantém o token e deixa as telas mostrarem o erro.
          else setSessao(null)
        })
    }
    return () => window.removeEventListener(SESSAO_EXPIRADA, sair)
  }, [])

  if (sessao === undefined && getToken()) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-white text-sm text-ink-500">
        Carregando…
      </div>
    )
  }

  return (
    <SessaoProvider valor={sessao ?? null}>
      <RouterProvider router={router} />
      {/* Aviso de versão nova e convite de instalação. */}
      <AvisosPwa />
    </SessaoProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Bootstrap />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)

// Depois de montar: o service worker não deve competir com o primeiro desenho.
registrarServiceWorker()
